import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, PaperPlaneRight, Spinner } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIAnalisisPage() {
  const [prompt, setPrompt] = useState("");
  const [contextType, setContextType] = useState("produksi");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim() || streaming) return;
    const userMsg = { role: "user", content: prompt };
    setMessages(prev => [...prev, userMsg]);
    setPrompt("");
    setStreaming(true);

    const assistantMsg = { role: "assistant", content: "" };
    setMessages(prev => [...prev, assistantMsg]);

    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/ai/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        credentials: "include",
        body: JSON.stringify({ prompt: userMsg.content, context_type: contextType }),
      });

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                accumulated += data.content;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: "assistant", content: accumulated };
                  return updated;
                });
              }
              if (data.done) break;
              if (data.error) {
                accumulated += `\n\nError: ${data.error}`;
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: "assistant", content: accumulated };
                  return updated;
                });
              }
            } catch { /* skip bad json */ }
          }
        }
      }
    } catch (err) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "assistant", content: "Gagal menghubungi AI. Silakan coba lagi." };
        return updated;
      });
    }
    setStreaming(false);
  };

  const presetPrompts = [
    { label: "Analisis Tren Produksi", prompt: "Analisis tren produksi tambak udang berdasarkan data laporan harian. Identifikasi pola, anomali, dan berikan rekomendasi perbaikan." },
    { label: "Prediksi Hasil Panen", prompt: "Berdasarkan data produksi dan panen sebelumnya, prediksi hasil panen berikutnya. Berikan estimasi berat, ukuran, dan waktu optimal panen." },
    { label: "Evaluasi Keuangan", prompt: "Evaluasi kondisi keuangan koperasi. Analisis tren pemasukan/pengeluaran, profitabilitas, dan berikan rekomendasi efisiensi biaya." },
    { label: "Rekomendasi Pakan", prompt: "Analisis pola pemberian pakan dan kondisi air. Berikan rekomendasi jenis pakan, dosis optimal, dan jadwal pemberian yang efisien." },
  ];

  return (
    <div data-testid="ai-analisis-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>
          AI Analisis
        </h1>
        <p className="text-sm text-slate-500 mt-1">Analisis cerdas berbasis AI untuk operasional tambak</p>
      </div>

      {/* Context selector + presets */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={contextType} onValueChange={setContextType}>
          <SelectTrigger data-testid="select-ai-context" className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="produksi">Data Produksi</SelectItem>
            <SelectItem value="keuangan">Data Keuangan</SelectItem>
            <SelectItem value="panen">Data Panen</SelectItem>
            <SelectItem value="general">Umum</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex flex-wrap gap-2">
          {presetPrompts.map((pp, i) => (
            <Button
              key={i}
              data-testid={`ai-preset-${i}`}
              variant="outline"
              size="sm"
              onClick={() => setPrompt(pp.prompt)}
              className="text-xs border-slate-300 text-slate-600 hover:border-[#B8860B] hover:text-[#B8860B]"
            >
              {pp.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <div className="h-[400px] overflow-y-auto p-6 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full text-slate-400">
                <Brain size={48} weight="duotone" className="mb-3 text-[#B8860B]/30" />
                <p className="text-sm">Mulai percakapan dengan AI Analis P3UW</p>
                <p className="text-xs mt-1">Pilih preset atau ketik pertanyaan Anda</p>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed
                    ${msg.role === "user"
                      ? "bg-[#B8860B] text-white"
                      : "bg-[#F0FDF4] border border-emerald-200 text-slate-800"
                    }`}
                >
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <Brain size={14} weight="bold" className="text-[#008080]" />
                      <span className="text-[10px] font-semibold text-[#008080] uppercase tracking-wider">AI Analis</span>
                    </div>
                  )}
                  <div className="whitespace-pre-wrap">{msg.content || (streaming && i === messages.length - 1 ? <Spinner size={16} className="animate-spin text-[#008080]" /> : "")}</div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-slate-200 p-4">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <Textarea
                data-testid="ai-prompt-input"
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="Tanyakan tentang produksi, keuangan, atau rekomendasi..."
                className="min-h-[44px] max-h-32 resize-none"
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(e); } }}
              />
              <Button
                type="submit"
                data-testid="ai-submit-btn"
                disabled={streaming || !prompt.trim()}
                className="bg-[#B8860B] hover:bg-[#9A7209] text-white px-4 shrink-0"
              >
                {streaming ? <Spinner size={18} className="animate-spin" /> : <PaperPlaneRight size={18} />}
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
